import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LogOut, RefreshCw, Sparkles, Copy, MessageCircle, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";

const STATUS_OPTIONS = ["new", "contacted", "qualified", "won", "lost"];
const STORAGE_KEY = "wz_admin_pwd";

function StatusBadge({ status }) {
  const map = {
    new: "bg-[#00D4FF]/15 text-[#00D4FF]",
    contacted: "bg-[#FFC857]/20 text-[#B37E00]",
    qualified: "bg-[#21F59B]/15 text-[#0E9F63]",
    won: "bg-[#21F59B]/25 text-[#0E7A4F]",
    lost: "bg-slate-200 text-slate-600",
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || "bg-slate-100 text-slate-600"}`}>{status}</span>;
}

function PriorityBadge({ priority }) {
  if (!priority) return <span className="text-xs text-slate-400">—</span>;
  const map = {
    high: "bg-red-100 text-red-700 border-red-200",
    medium: "bg-amber-100 text-amber-700 border-amber-200",
    low: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider border ${map[priority] || map.medium}`}>{priority}</span>;
}

function AiPanel({ lead, pwd, onUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);

  async function regenerate() {
    setRegenerating(true);
    try {
      const res = await api.post(`/admin/leads/${lead.id}/generate-reply`, {}, { headers: { "X-Admin-Password": pwd } });
      onUpdated(res.data);
      toast.success("New reply drafted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate reply");
    } finally {
      setRegenerating(false);
    }
  }

  async function reanalyze() {
    setReanalyzing(true);
    try {
      const res = await api.post(`/admin/leads/${lead.id}/reanalyze`, {}, { headers: { "X-Admin-Password": pwd } });
      onUpdated(res.data);
      toast.success("Lead re-analyzed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-analyze failed");
    } finally {
      setReanalyzing(false);
    }
  }

  function copyReply() {
    if (!lead.ai_reply_draft) return;
    navigator.clipboard.writeText(lead.ai_reply_draft).then(
      () => toast.success("Copied to clipboard"),
      () => toast.error("Copy failed")
    );
  }

  const waLink = lead.phone
    ? `https://wa.me/91${lead.phone}?text=${encodeURIComponent(lead.ai_reply_draft || "")}`
    : "#";

  const hasAi = lead.ai_summary || lead.ai_reply_draft;

  return (
    <div className="mt-2">
      <button
        data-testid={`admin-ai-toggle-${lead.id}`}
        onClick={() => setExpanded((v) => !v)}
        className="text-xs inline-flex items-center gap-1.5 text-[#00D4FF] hover:text-[#0E9F63]"
      >
        <Sparkles size={12} /> {expanded ? "Hide AI insights" : (hasAi ? "AI insights" : "AI insights (pending)")} {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {expanded && (
        <div data-testid={`admin-ai-panel-${lead.id}`} className="mt-3 rounded-xl bg-mist p-4 border border-slate-100 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="text-xs text-slate-500 uppercase tracking-wider">AI summary</div>
            <PriorityBadge priority={lead.ai_priority} />
          </div>
          <div data-testid={`admin-ai-summary-${lead.id}`} className="text-sm text-graphite leading-relaxed">
            {lead.ai_summary || <span className="text-slate-400 italic">Analysis in progress — click "Re-analyze" if it doesn't appear shortly.</span>}
          </div>

          <div className="pt-2 border-t border-slate-200">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Suggested WhatsApp reply</div>
            <textarea
              data-testid={`admin-ai-reply-${lead.id}`}
              value={lead.ai_reply_draft || ""}
              onChange={(e) => onUpdated({ ...lead, ai_reply_draft: e.target.value })}
              rows={5}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-graphite focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
              placeholder="No reply drafted yet."
            />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                data-testid={`admin-ai-regenerate-${lead.id}`}
                onClick={regenerate}
                disabled={regenerating}
                className="text-xs inline-flex items-center gap-1.5 rounded-full bg-[#071A2D] text-white px-3 py-1.5 hover:opacity-90 disabled:opacity-50"
              >
                <Sparkles size={12} /> {regenerating ? "Drafting..." : "Regenerate"}
              </button>
              <button
                data-testid={`admin-ai-reanalyze-${lead.id}`}
                onClick={reanalyze}
                disabled={reanalyzing}
                className="text-xs inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 text-graphite px-3 py-1.5 hover:border-[#00D4FF] disabled:opacity-50"
              >
                <RotateCcw size={12} /> {reanalyzing ? "Analysing..." : "Re-analyze"}
              </button>
              <button
                data-testid={`admin-ai-copy-${lead.id}`}
                onClick={copyReply}
                disabled={!lead.ai_reply_draft}
                className="text-xs inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 text-graphite px-3 py-1.5 hover:border-[#00D4FF] disabled:opacity-50"
              >
                <Copy size={12} /> Copy
              </button>
              <a
                data-testid={`admin-ai-whatsapp-${lead.id}`}
                href={waLink}
                target="_blank"
                rel="noreferrer"
                className={`text-xs inline-flex items-center gap-1.5 rounded-full bg-[#21F59B] text-[#071A2D] px-3 py-1.5 font-medium ${lead.ai_reply_draft ? "" : "pointer-events-none opacity-50"}`}
              >
                <MessageCircle size={12} /> Send on WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminLeads() {
  const [pwd, setPwd] = useState(localStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loginErr, setLoginErr] = useState("");

  async function fetchLeads(password) {
    setLoading(true);
    try {
      const res = await api.get("/admin/leads", { headers: { "X-Admin-Password": password } });
      setLeads(res.data);
      setAuthed(true);
      localStorage.setItem(STORAGE_KEY, password);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthed(false);
        setLoginErr("Invalid password");
        localStorage.removeItem(STORAGE_KEY);
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (pwd) fetchLeads(pwd);
     
  }, []);

  async function updateStatus(id, status) {
    try {
      await api.patch(`/admin/leads/${id}`, { status }, { headers: { "X-Admin-Password": pwd } });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Status updated");
    } catch {
      toast.error("Update failed");
    }
  }

  function replaceLead(updated) {
    setLeads((ls) => ls.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
  }

  function logout() {
    localStorage.removeItem(STORAGE_KEY);
    setPwd("");
    setAuthed(false);
    setLeads([]);
  }

  if (!authed) {
    return (
      <div className="min-h-[70vh] bg-mist py-24">
        <div className="max-w-md mx-auto px-5">
          <div className="rounded-2xl bg-white shadow-card-soft p-7 border border-slate-100">
            <h1 className="font-heading text-2xl text-graphite">Admin — leads</h1>
            <p className="mt-2 text-sm text-slate-600">Enter the shared admin password to continue.</p>
            <form
              onSubmit={(e) => { e.preventDefault(); setLoginErr(""); fetchLeads(pwd); }}
              className="mt-5 flex flex-col gap-3"
            >
              <input
                type="password"
                data-testid="admin-password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                className="rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                placeholder="Admin password"
                autoFocus
              />
              {loginErr && <div data-testid="admin-login-error" className="text-sm text-red-600">{loginErr}</div>}
              <button data-testid="admin-login" type="submit" className="btn-gradient px-5 py-3 rounded-full text-sm">Sign in</button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-mist py-12">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl text-graphite">Leads</h1>
            <p className="text-sm text-slate-600 mt-1">{leads.length} total · AI insights powered by gpt-5.2</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={() => fetchLeads(pwd)} className="text-sm inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 hover:border-[#00D4FF]">
              <RefreshCw size={14} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout} className="text-sm inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 hover:border-slate-400">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-100 shadow-card-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-mist text-slate-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Company</th>
                  <th className="px-4 py-3 font-medium">Phone</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Source</th>
                  <th className="px-4 py-3 font-medium">Priority</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody data-testid="admin-leads-table">
                {loading ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">Loading...</td></tr>
                ) : leads.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">No leads yet.</td></tr>
                ) : (
                  leads.map((l) => (
                    <React.Fragment key={l.id}>
                      <tr className="border-t border-slate-100 align-top">
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                        <td className="px-4 py-3 text-graphite font-medium">{l.name}</td>
                        <td className="px-4 py-3 text-slate-600">{l.company || "—"}</td>
                        <td className="px-4 py-3 text-slate-600">+91 {l.phone}</td>
                        <td className="px-4 py-3 text-slate-600">{l.email || "—"}</td>
                        <td className="px-4 py-3 text-slate-600">{l.source}</td>
                        <td className="px-4 py-3"><PriorityBadge priority={l.ai_priority} /></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <StatusBadge status={l.status} />
                            <select
                              value={l.status}
                              onChange={(e) => updateStatus(l.id, e.target.value)}
                              className="text-xs rounded-full border border-slate-200 px-2 py-1 bg-white"
                              data-testid={`admin-status-${l.id}`}
                            >
                              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </div>
                        </td>
                      </tr>
                      <tr className="border-t border-slate-50">
                        <td colSpan={8} className="px-4 pb-4">
                          {(l.site_type || l.charger_count) && (
                            <div data-testid={`admin-chargehub-info-${l.id}`} className="mt-1 text-xs text-slate-600">
                              <span className="px-2 py-0.5 rounded-full bg-[#FFC857]/15 border border-[#FFC857]/40 text-[#B37E00] font-medium mr-2">ChargeHub</span>
                              {l.site_type && <>Site type: <span className="text-graphite font-medium">{l.site_type}</span></>}
                              {l.site_type && l.charger_count && <span className="mx-1.5">·</span>}
                              {l.charger_count && <>Chargers: <span className="text-graphite font-medium">{l.charger_count}</span></>}
                            </div>
                          )}
                          <AiPanel lead={l} pwd={pwd} onUpdated={replaceLead} />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
